import { UPGRADES, type UpgradeId } from '../core/balance';
import { autowalkRate, ceiling, medals, purchaseStatus } from '../core/engine';
import type { GameState } from '../core/state';
import { storageKey } from '../shared/lib/edition';
import { storage } from '../shared/lib/storage';

/**
 * Progressive disclosure. The game starts as a single number; each piece of interface
 * appears the first time its condition is met and then stays, even if the condition stops
 * holding (say, after spending points).
 *
 * Every reveal carries a one-line "whisper" shown when it first appears. That's the only
 * tutorial the game has.
 */

export type Piece =
  | 'points'
  | 'caption'
  | 'medals'
  | 'menu'
  | 'shop'
  | 'radar'
  | 'flip'
  | 'collection'
  | 'trial'
  | 'auto'
  | 'edge';

interface Rule {
  piece: Piece;
  when: (s: GameState) => boolean;
  whisper: string;
}

/**
 * Ordered as a new player meets them. Gated on distance walked rather than points, because
 * famous numbers pay in lumps (the very first step lands on 1, worth 21 points).
 */
const RULES: readonly Rule[] = [
  {
    piece: 'points',
    when: (s) => s.maxVisited >= 4,
    whisper: 'new ground is worth something.',
  },
  {
    piece: 'caption',
    when: (s) => s.maxVisited >= 2,
    whisper: 'two. it divides by nothing but itself.',
  },
  {
    piece: 'medals',
    when: (s) => s.maxVisited >= 6,
    whisper: 'every prime you reach is yours to keep.',
  },
  { piece: 'menu', when: (s) => s.taps >= 15, whisper: '' },
  {
    piece: 'shop',
    when: (s) => s.maxVisited >= 8 && s.lifetimePoints >= 30,
    whisper: 'points can be spent.',
  },
  { piece: 'radar', when: (s) => s.upgrades.radar > 0, whisper: 'the line comes into view.' },
  {
    piece: 'edge',
    when: (s) => s.maxVisited >= ceiling(s) - 15,
    whisper: 'the line ends somewhere ahead.',
  },
  {
    piece: 'flip',
    when: (s) => s.upgrades.inverter > 0,
    whisper: 'there is ground behind zero, too.',
  },
  {
    piece: 'collection',
    when: (s) => medals(s) >= 10,
    whisper: 'ten medals. worth keeping a record.',
  },
  { piece: 'trial', when: (s) => s.taps >= 80, whisper: 'how fast can you go?' },
  { piece: 'auto', when: (s) => autowalkRate(s) > 0, whisper: 'the line walks itself now.' },
];

const KEY = storageKey('reveal');

function load(): { pieces: Piece[]; upgrades: UpgradeId[] } {
  try {
    const raw = JSON.parse(storage.get(KEY) ?? '{}') as {
      pieces?: Piece[];
      upgrades?: UpgradeId[];
    };
    return { pieces: raw.pieces ?? [], upgrades: raw.upgrades ?? [] };
  } catch {
    return { pieces: [], upgrades: [] };
  }
}

class Reveal {
  pieces = $state<Piece[]>([]);
  upgrades = $state<UpgradeId[]>([]);
  whisper = $state<{ id: number; text: string } | null>(null);
  #whisperId = 0;

  constructor() {
    Object.assign(this, load());
  }

  has(p: Piece): boolean {
    return this.pieces.includes(p);
  }

  /** Re-evaluates every rule against the state. Cheap; runs after each change. */
  update(s: GameState): void {
    let changed = false;
    for (const r of RULES) {
      if (!this.pieces.includes(r.piece) && r.when(s)) {
        this.pieces.push(r.piece);
        if (r.whisper) this.say(r.whisper);
        changed = true;
      }
    }
    // Upgrades surface once they're unlocked and within reach: cost no more than twice
    // what you've earned so far. Like a shop that stocks up as you grow.
    for (const u of UPGRADES) {
      if (this.upgrades.includes(u.id)) continue;
      const st = purchaseStatus(s, u.id);
      if (st.kind === 'available' && st.cost <= Math.max(2 * s.lifetimePoints, 30)) {
        this.upgrades.push(u.id);
        changed = true;
      }
    }
    if (changed) this.#save();
  }

  say(text: string): void {
    this.whisper = { id: ++this.#whisperId, text };
  }

  reset(): void {
    this.pieces = [];
    this.upgrades = [];
    this.whisper = null;
    this.#save();
  }

  #save(): void {
    storage.set(KEY, JSON.stringify({ pieces: this.pieces, upgrades: this.upgrades }));
  }
}

export const reveal = new Reveal();
